import type { ColumnDef } from '@tanstack/react-table';
import {
  Badge,
  DataTableHierarchyCell,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  createAppColumnHelper,
  type DataTableFeatures
} from 'design-system';
import { LockKeyholeIcon } from 'lucide-react';

import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';

import type { MultiGroupTableRowType, SymbolPropertyConfig } from '../../types';

export type MultiGroupDataRow = {
  kind: 'multiGroup';
  id: string;
  value: MultiGroupTableRowType;
  restricted: boolean;
};

export type MultiGroupSectionRow = {
  kind: 'section';
  id: string;
  name: string;
  subRows: MultiGroupDataRow[];
};

export type MultiGroupTableRow = MultiGroupSectionRow | MultiGroupDataRow;

type MultiGroupTableColumnOptions = {
  formatDate: (date: string) => string;
  isLoading: boolean;
  properties: SymbolPropertyConfig[];
};

const columnHelper = createAppColumnHelper<MultiGroupTableRow>();

export function collectMultiGroupColumns(
  sections: Array<{ rows: MultiGroupTableRowType[] }>
): SymbolPropertyConfig[] {
  const seen = new Set<string>();
  const columns: SymbolPropertyConfig[] = [];

  for (const section of sections) {
    for (const row of section.rows) {
      for (const property of row.symbolBProperties) {
        if (seen.has(property.name)) continue;
        seen.add(property.name);
        columns.push(property);
      }
    }
  }

  return columns;
}

function renderRestrictedValue() {
  const badge = (
    <Badge
      aria-label='Restricted market data'
      render={
        <button
          aria-label='Restricted market data'
          type='button'
        />
      }
      variant='neutralOutline'
    >
      <LockKeyholeIcon />
      <span className='sr-only'>Restricted</span>
    </Badge>
  );

  return (
    <Tooltip>
      <TooltipTrigger render={badge} />
      <TooltipContent>
        <span>You don&apos;t have access to this.</span>
        <a
          className='font-bold underline'
          href='https://www.general-index.com/contact-us'
          rel='noopener noreferrer'
          target='_blank'
        >
          Request access
        </a>
      </TooltipContent>
    </Tooltip>
  );
}

function renderLoadingCell(label: string) {
  return (
    <output
      aria-label={label}
      className='block w-full'
    >
      <Skeleton className='ml-auto h-5 w-16' />
    </output>
  );
}

function renderValue({
  row,
  value,
  isLoading,
  label,
  change = false
}: {
  row: MultiGroupTableRow;
  value: string | null | undefined;
  isLoading: boolean;
  label: string;
  change?: boolean;
}) {
  if (row.kind === 'section') return null;
  if (isLoading && !value) return renderLoadingCell(label);
  if (row.restricted) return renderRestrictedValue();
  if (!value) return null;

  const formatted = formatNumberWithPrecision(value);
  if (!change) return formatted;

  const numericValue = Number(value);
  let tone = 'text-muted-foreground';
  if (numericValue > 0) tone = 'text-gx-success';
  if (numericValue < 0) tone = 'text-gx-failure-light';
  return <span className={tone}>{formatted}</span>;
}

export function getMultiGroupTableColumns({
  formatDate,
  isLoading,
  properties
}: MultiGroupTableColumnOptions): ColumnDef<DataTableFeatures, MultiGroupTableRow>[] {
  const shortTitleColumn = columnHelper.display({
    id: 'shortTitle',
    header: 'Short Title',
    cell: ({ row }) => (
      <DataTableHierarchyCell row={row}>
        {row.original.kind === 'section' ? (
          <span className='font-semibold'>{row.original.name}</span>
        ) : (
          row.original.value.shortTitle
        )}
      </DataTableHierarchyCell>
    )
  });

  const dateColumn = columnHelper.display({
    id: 'date',
    header: 'Date',
    meta: { align: 'end' },
    cell: ({ row }) => {
      if (row.original.kind === 'section') return null;
      const date = row.original.value.date;
      if (isLoading && !date) return renderLoadingCell('Loading multi-group date');
      return date ? formatDate(date) : null;
    }
  });

  const lastColumn = columnHelper.display({
    id: 'last',
    header: 'Last',
    meta: { align: 'end' },
    cell: ({ row }) =>
      renderValue({
        row: row.original,
        value: row.original.kind === 'multiGroup' ? row.original.value.last : null,
        isLoading,
        label: 'Loading Last'
      })
  });

  const changeColumn = columnHelper.display({
    id: 'change',
    header: 'Change',
    meta: { align: 'end' },
    cell: ({ row }) =>
      renderValue({
        row: row.original,
        value: row.original.kind === 'multiGroup' ? row.original.value.delta : null,
        isLoading,
        label: 'Loading Change',
        change: true
      })
  });

  const propertyColumns = properties.map(property =>
    columnHelper.display({
      id: `property:${property.name}`,
      header: property.label,
      meta: { align: 'end' },
      cell: ({ row }) =>
        renderValue({
          row: row.original,
          value:
            row.original.kind === 'multiGroup' ? row.original.value.bValues?.[property.name] : null,
          isLoading,
          label: `Loading ${property.label}`
        })
    })
  );

  return columnHelper.columns([
    shortTitleColumn,
    dateColumn,
    lastColumn,
    changeColumn,
    ...propertyColumns
  ]);
}
